import { useEffect, useRef, useState } from "react";
import { CheckCircle2, ChevronsUpDown, FolderInput, HelpCircle, Loader2, Pencil, Sparkles, Trash2, Upload, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import ConfirmDialog from "@/components/ConfirmDialog";
import ScanBulkEdit from "@/components/ScanBulkEdit";
import AiSortPreview from "@/components/AiSortPreview";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Thumb, openFile } from "@/components/Customers";
import ScanPreview from "@/components/ScanPreview";

const OK_TYPE = /\.(pdf|jpe?g|png|webp|heic|heif)$/i;

function CustomerPicker({ customers, onPick, testId, label = "File to customer" }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" size="sm" variant="outline" className="w-full justify-between" data-testid={testId}><span className="truncate">{label}</span> <ChevronsUpDown size={14} /></Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-72 bg-card" align="start">
        <Command>
          <CommandInput placeholder="Search customers" data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>No customer found.</CommandEmpty>
            {customers.map((c) => (
              <CommandItem key={c.key} value={`${c.name} ${c.city || ""} ${c.key}`} onSelect={() => { setOpen(false); onPick(c); }} data-testid={`${testId}-option`}>
                <span className="truncate">{c.name}</span><span className="ml-auto text-xs text-muted-foreground">{c.city}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function Results({ results }) {
  if (!results.length) return null;
  return (
    <div className="mt-4 border rounded-lg bg-card divide-y max-h-64 overflow-y-auto text-sm" data-testid="batch-results">
      {results.map((r, i) => (
        <div key={i} className="px-4 py-2 flex items-center gap-3" data-testid={`batch-result-${i}`}>
          {r.matched ? <CheckCircle2 size={15} className="text-emerald-400 shrink-0" /> : <HelpCircle size={15} className={`${r.error ? "text-red-400" : "text-amber-400"} shrink-0`} />}
          <span className="font-mono truncate flex-1">{r.name}</span>
          <span className="text-xs text-muted-foreground truncate">{r.error || (r.matched ? `→ ${r.customer}${r.invoice ? ` (${r.invoice})` : ""}` : "Unsorted")}</span>
        </div>
      ))}
    </div>
  );
}

export default function BatchScans({ customers, onFiled }) {
  const [unsorted, setUnsorted] = useState(null);
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState(null);
  const [pending, setPending] = useState(null);
  const [view, setView] = useState("unsorted");
  const [selected, setSelected] = useState(new Set());
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [resorting, setResorting] = useState(false);
  const [aiRows, setAiRows] = useState(null);
  const [aiReading, setAiReading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const folder = useRef(null);
  const files = useRef(null);

  const load = () => {
    setSelected(new Set());
    return api.get(view === "unsorted" ? "/scans/unsorted" : "/scans/recent").then((r) => setUnsorted(r.data)).catch((e) => toast.error(formatError(e)));
  };
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const removeSelected = async () => {
    try {
      const { data } = await api.post("/scans/bulk-delete", { ids: [...selected] });
      toast.success(`${data.deleted} scan${data.deleted === 1 ? "" : "s"} deleted`);
      load(); onFiled();
    } catch (e) { toast.error(formatError(e)); }
    setDelOpen(false);
  };
  useEffect(() => { setUnsorted(null); load(); }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickFiles = async (list) => {
    const all = [...list];
    const todo = all.filter((f) => OK_TYPE.test(f.name));
    const skipped = all.filter((f) => !OK_TYPE.test(f.name) && !f.name.startsWith(".")).map((f) => ({ name: f.name, error: "Skipped — not a PDF or photo" }));
    setResults(skipped);
    if (!todo.length) return toast.error("No PDFs or photos found in that selection");
    try {
      const { data } = await api.post("/scans/preview", { files: todo.map((f) => ({ name: f.name })) });
      setPending(data.map((r, i) => ({ ...r, file: todo[i], override: "" })));
    } catch (e) { toast.error(formatError(e)); }
  };

  const override = async (i, value) => {
    const r = pending[i];
    try {
      const { data } = await api.post("/scans/preview", { files: [{ name: r.name, invoice_number: value || null }] });
      setPending((p) => p.map((x, j) => (j === i ? { ...x, ...data[0], override: value } : x)));
    } catch (e) { toast.error(formatError(e)); }
  };

  const upload = async () => {
    const todo = pending;
    setPending(null);
    const out = [...results];
    for (let i = 0; i < todo.length; i += 1) {
      setProgress({ done: i, total: todo.length });
      const form = new FormData();
      form.append("file", todo[i].file, todo[i].name);
      form.append("invoice_number", todo[i].override || "");
      try {
        const { data } = await api.post("/scans/batch", form);
        out.push({ name: todo[i].name, matched: data.matched, customer: data.customer_name, invoice: data.invoice_number });
      } catch (e) { out.push({ name: todo[i].name, error: formatError(e) }); }
    }
    setProgress(null);
    setResults(out);
    const n = out.filter((r) => r.matched).length;
    toast.success(`${n} of ${todo.length} scans filed to customers${n < todo.length ? `, ${todo.length - n} left in Unsorted` : ""}`);
    load(); onFiled();
  };

  const resort = async () => {
    setResorting(true);
    try {
      const { data } = await api.post("/scans/resort");
      toast.success(data.filed ? `Filed ${data.filed} of ${data.checked} scan${data.checked === 1 ? "" : "s"} to customers` : "No new matches — nothing could be sorted");
      await load(); onFiled();
    } catch (e) { toast.error(formatError(e)); }
    setResorting(false);
  };

  const aiRead = async () => {
    const ids = selected.size ? [...selected] : (unsorted || []).map((f) => f.id);
    if (!ids.length) return;
    setAiReading(true);
    try {
      const { data } = await api.post("/scans/ai-read", { ids });
      setAiRows(data);
    } catch (e) { toast.error(formatError(e)); }
    setAiReading(false);
  };

  const aiApply = async (filedRows) => {
    setAiBusy(true);
    let n = 0;
    for (const r of filedRows) {
      try { await api.post(`/scans/${r.id}/assign`, { customer_key: r.customer_key, invoice_number: r.invoice_number || "" }); n += 1; } catch (e) { /* skip */ }
    }
    setAiBusy(false);
    setAiRows(null);
    toast.success(`Filed ${n} scan${n === 1 ? "" : "s"} to customers`);
    load(); onFiled();
  };

  const assign = async (f, c) => {
    try {
      await api.post(`/scans/${f.id}/assign`, { customer_key: c.key });
      toast.success(`Filed to ${c.name}`);
      load(); onFiled();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="fade-up" data-testid="batch-scans">
      <p className="text-xs font-mono uppercase tracking-widest text-cyan-400">Batch scan folder</p>
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-1">Sort scans automatically</h2>
      <p className="text-sm text-muted-foreground mt-1">Name each scan by its invoice number (e.g. G26100708.pdf) — or by the customer's name (e.g. Golden Acres Living Rehab.pdf) if there's no invoice number. You'll see where each scan will go before anything uploads. Matching scans go into that customer's folder; the rest wait in Unsorted.</p>
      <div className="mt-5 border rounded-lg bg-card p-4 flex flex-wrap items-center gap-3">
        <input ref={folder} type="file" webkitdirectory="" directory="" multiple className="hidden" onChange={(e) => { pickFiles(e.target.files); e.target.value = ""; }} data-testid="batch-folder-input" />
        <input ref={files} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => { pickFiles(e.target.files); e.target.value = ""; }} data-testid="batch-files-input" />
        <Button onClick={() => folder.current.click()} disabled={!!progress} data-testid="batch-folder-button" className="hover:bg-blue-600 hover:text-white"><FolderInput size={16} /> Choose scan folder</Button>
        <Button variant="outline" onClick={() => files.current.click()} disabled={!!progress} data-testid="batch-files-button"><Upload size={16} /> Choose files</Button>
        {progress && <span className="text-sm text-muted-foreground flex items-center gap-2" data-testid="batch-progress"><Loader2 size={15} className="animate-spin" /> Sorting {progress.done + 1} of {progress.total}…</span>}
      </div>
      {pending && <ScanPreview rows={pending} onOverride={override} onUpload={upload} onCancel={() => setPending(null)} />}
      <Results results={results} />
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-md border p-0.5" data-testid="scan-view-toggle">
          {[["unsorted", "Unsorted"], ["recent", "Recent uploads"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`scan-view-${k}`}
              className={`px-3 py-1 text-sm rounded transition-colors ${view === k ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>{l}</button>
          ))}
        </div>
        <span className="text-sm text-muted-foreground" data-testid="unsorted-heading">{unsorted ? unsorted.length : "…"} scan{unsorted?.length === 1 ? "" : "s"}</span>
        {!!unsorted?.length && (
          <label className="flex items-center gap-2 text-sm cursor-pointer ml-2">
            <Checkbox className="data-[state=indeterminate]:bg-primary/40" checked={selected.size === unsorted.length ? true : selected.size ? "indeterminate" : false}
              onCheckedChange={() => setSelected(selected.size === unsorted.length ? new Set() : new Set(unsorted.map((f) => f.id)))} data-testid="batch-select-all" />
            {selected.size ? <span data-testid="batch-selected-count">{selected.size} selected</span> : "Select all"}
          </label>
        )}
        {view === "unsorted" && unsorted?.length > 1 && (
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set(unsorted.slice(0, 25).map((f) => f.id)))} data-testid="batch-select-25" className="hover:bg-secondary hover:text-foreground">
            Select 25
          </Button>
        )}
        {selected.size > 0 && <>
          <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} data-testid="batch-edit-selected-button" className="hover:bg-secondary hover:text-foreground"><Pencil size={14} /> Edit selected</Button>
          <Button size="sm" variant="outline" onClick={() => setDelOpen(true)} data-testid="batch-delete-selected-button" className="text-red-400 hover:bg-red-500/10 hover:text-red-400"><Trash2 size={14} /> Delete selected</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="batch-clear-selection">Clear</Button>
        </>}
        {view === "unsorted" && !!unsorted?.length && (
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={aiRead} disabled={aiReading || !!aiRows} data-testid="batch-ai-sort-button" className="hover:bg-violet-500/10 hover:text-violet-300 border-violet-500/40">
              {aiReading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} className="text-violet-400" />} {selected.size ? `Read & sort selected (${selected.size})` : "Read & sort with AI"}
            </Button>
            <Button size="sm" variant="outline" onClick={resort} disabled={resorting} data-testid="batch-resort-button" className="hover:bg-secondary hover:text-foreground">
              {resorting ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Re-sort Unsorted
            </Button>
          </div>
        )}
      </div>
      {aiReading && !aiRows && <p className="mt-3 text-sm text-muted-foreground flex items-center gap-2" data-testid="ai-reading-note"><Loader2 size={14} className="animate-spin" /> Reading scans with AI… this can take a moment per scan.</p>}
      {aiRows && <AiSortPreview rows={aiRows} busy={aiBusy} onCancel={() => setAiRows(null)} onConfirm={aiApply} />}
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="unsorted-files">
        {!unsorted && <Loader2 className="animate-spin text-primary" />}
        {unsorted?.map((f) => (
          <div key={f.id} className={`space-y-1.5 relative rounded-md ${selected.has(f.id) ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""}`} data-testid={`unsorted-file-${f.id}`}>
            <div className="absolute top-2 left-2 z-10 rounded bg-background/80 p-1"><Checkbox checked={selected.has(f.id)} onCheckedChange={() => toggle(f.id)} data-testid={`batch-select-${f.id}`} aria-label={`Select ${f.original_filename}`} /></div>
            <Thumb file={f} onOpen={openFile} />
            <p className="font-mono text-xs truncate" title={f.original_filename}>{f.original_filename}</p>
            {view === "recent" && <p className="text-[11px] text-muted-foreground truncate" data-testid={`batch-file-info-${f.id}`}>{f.customer_name || "Unsorted"}{f.invoice_number ? ` · #${f.invoice_number}` : ""}</p>}
            {view === "unsorted" && <CustomerPicker customers={customers} onPick={(c) => assign(f, c)} testId={`assign-${f.id}`} />}
          </div>
        ))}
        {unsorted?.length === 0 && <p className="col-span-full text-sm text-muted-foreground" data-testid="unsorted-empty">{view === "unsorted" ? "Nothing unsorted." : "No scans uploaded yet."}</p>}
      </div>
      <ScanBulkEdit ids={[...selected]} open={editOpen} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); load(); onFiled(); }}
        Picker={(p) => <CustomerPicker customers={customers} {...p} />} />
      <ConfirmDialog open={delOpen} onOpenChange={setDelOpen} onConfirm={removeSelected} testId="batch-delete-scans"
        title={`Delete ${selected.size} scan${selected.size === 1 ? "" : "s"}?`} description="The selected scans will be removed from customer folders and the Customer Scans table." />
    </div>
  );
}
